import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Search, RefreshCcw, Trash2, Mail, Phone, Building2 } from "lucide-react";

const inputCls = "rounded-xl h-11";
const selectCls =
  "rounded-xl h-11 w-full px-3 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none";

const SOURCE_META = {
  lead: { label: "Lead", cls: "bg-brand-orange/15 text-brand-orange border-brand-orange/30" },
  customer: { label: "Customer", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [picFilter, setPicFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (sourceFilter) params.source = sourceFilter;
      const [contactsRes, teamRes] = await Promise.all([
        api.get("/admin/contacts", { params }),
        api.get("/admin/team").catch(() => ({ data: { items: [] } })),
      ]);
      let list = contactsRes.data.items || [];
      if (picFilter) list = list.filter((c) => c.pic_id === picFilter);
      setContacts(list);
      setTeam(teamRes.data.items || []);
    } catch {
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [q, sourceFilter, picFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateSelected = async (updates) => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`/admin/contacts/${selected.id}`, updates);
      setContacts((arr) => arr.map((x) => (x.id === selected.id ? { ...x, ...data } : x)));
      setSelected((s) => ({ ...s, ...data }));
      toast.success("Contact updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/admin/contacts/${toDelete.id}`);
      setContacts((arr) => arr.filter((x) => x.id !== toDelete.id));
      setSelected(null);
      toast.success("Contact deleted");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setToDelete(null);
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="admin-contacts-page">
      <AdminHeader />

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-[10px] md:text-xs uppercase tracking-[0.45em] text-brand-orange font-semibold">CRM</p>
            <h1 className="text-3xl md:text-4xl font-semibold text-brand-navy mt-1" data-testid="admin-contacts-heading">
              Contacts
            </h1>
            <p className="mt-2 text-sm text-brand-slate/75">
              Everyone who has come through Vanguard Beacon — leads from the landing page and customers created in the pipeline.
            </p>
          </div>
          <button
            onClick={fetchAll}
            className="inline-flex items-center gap-2 border border-brand-navy/20 px-5 py-2.5 text-xs font-semibold text-brand-navy hover:bg-white rounded-full transition-colors"
            data-testid="contacts-refresh-button"
          >
            <RefreshCcw size={14} /> Refresh
          </button>
        </div>

        <div className="bg-white border border-brand-navy/10 p-4 md:p-5 mb-6 grid md:grid-cols-12 gap-3 rounded-2xl">
          <div className="md:col-span-6 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-slate/50" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or email…"
              className="pl-9 rounded-xl h-11"
              data-testid="contacts-search-input"
            />
          </div>
          <div className="md:col-span-3">
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className={selectCls}
              data-testid="contacts-filter-source"
            >
              <option value="">All sources</option>
              <option value="lead">Leads</option>
              <option value="customer">Customers</option>
            </select>
          </div>
          <div className="md:col-span-3">
            <select
              value={picFilter}
              onChange={(e) => setPicFilter(e.target.value)}
              className={selectCls}
              data-testid="contacts-filter-pic"
            >
              <option value="">All PICs</option>
              {team.map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
            </select>
          </div>
        </div>

        <div className="bg-white border border-brand-navy/10 overflow-hidden rounded-2xl">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-brand-cream/60">
                  <TableHead className="text-brand-navy">Name</TableHead>
                  <TableHead className="text-brand-navy">Company</TableHead>
                  <TableHead className="text-brand-navy hidden md:table-cell">Email</TableHead>
                  <TableHead className="text-brand-navy hidden lg:table-cell">Phone</TableHead>
                  <TableHead className="text-brand-navy">Source</TableHead>
                  <TableHead className="text-brand-navy">PIC</TableHead>
                  <TableHead className="text-brand-navy text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-brand-slate/60">Loading…</TableCell></TableRow>
                )}
                {!loading && contacts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-brand-slate/60" data-testid="contacts-empty-state">
                      No contacts yet. Leads from the landing page and customers you create will appear here automatically.
                    </TableCell>
                  </TableRow>
                )}
                {!loading && contacts.map((c) => {
                  const src = SOURCE_META[c.source] || SOURCE_META.lead;
                  return (
                    <TableRow
                      key={c.id}
                      className="cursor-pointer hover:bg-brand-cream/40"
                      onClick={() => setSelected(c)}
                      data-testid={`contact-row-${c.id}`}
                    >
                      <TableCell className="font-medium text-brand-navy">{c.name}</TableCell>
                      <TableCell className="text-brand-slate">
                        <div className="flex items-center gap-2">
                          <Building2 size={14} className="text-brand-slate/50" />
                          {c.account_name || "—"}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-brand-slate text-sm">{c.email || "—"}</TableCell>
                      <TableCell className="hidden lg:table-cell text-brand-slate text-sm">{c.phone || "—"}</TableCell>
                      <TableCell>
                        <Badge className={`${src.cls} rounded-full`} data-testid={`contact-source-${c.id}`}>{src.label}</Badge>
                      </TableCell>
                      <TableCell className="text-brand-slate text-sm" data-testid={`contact-pic-${c.id}`}>
                        {c.pic_name || <span className="text-brand-slate/40">Unassigned</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); setToDelete(c); }}
                          className="text-brand-slate/60 hover:text-red-600 p-1"
                          aria-label="Delete"
                          data-testid={`contact-delete-${c.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="contact-detail-sheet">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="text-brand-navy">{selected.name}</SheetTitle>
                <SheetDescription>
                  {selected.email || "No email"}
                  {selected.account_name && <> · {selected.account_name}</>}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-4 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`${(SOURCE_META[selected.source] || SOURCE_META.lead).cls} rounded-full`}>
                    {(SOURCE_META[selected.source] || SOURCE_META.lead).label}
                  </Badge>
                  {selected.deal_stage && (
                    <Badge className="bg-brand-navy text-white rounded-full">Deal: {selected.deal_stage}</Badge>
                  )}
                </div>
                <ContactField label="Name" value={selected.name} onSave={(v) => updateSelected({ name: v })} />
                <ContactField label="Email" value={selected.email || ""} onSave={(v) => updateSelected({ email: v })} type="email" />
                <ContactField label="Phone" value={selected.phone || ""} onSave={(v) => updateSelected({ phone: v })} />
                <ContactField label="Title" value={selected.title || ""} onSave={(v) => updateSelected({ title: v })} />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-brand-slate/60 mb-1">PIC</p>
                  <p className="text-brand-navy text-sm">
                    {selected.pic_name || <span className="text-brand-slate/40">Unassigned</span>}
                  </p>
                  {selected.deal_id && (
                    <p className="text-[11px] text-brand-slate/50 mt-1">
                      PIC is inherited from the linked deal. Change it on the customer page.
                    </p>
                  )}
                </div>
                <div className="flex gap-2 pt-2">
                  {selected.email && (
                    <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2 bg-brand-navy text-white px-4 py-2 text-xs font-semibold rounded-full">
                      <Mail size={12} /> Email
                    </a>
                  )}
                  {selected.phone && (
                    <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-2 border border-brand-navy/20 text-brand-navy px-4 py-2 text-xs font-semibold rounded-full">
                      <Phone size={12} /> Call
                    </a>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        title="Delete this contact?"
        description={toDelete ? `${toDelete.name} will be removed from your contacts list.` : ""}
        confirmLabel="Delete"
        destructive
        onConfirm={doDelete}
      />
    </div>
  );
}

function ContactField({ label, value, type = "text", onSave }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => setV(value ?? ""), [value]);
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.18em] text-brand-slate/60 mb-1">{label}</p>
      <Input
        type={type}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => v !== (value ?? "") && onSave(v)}
        className={inputCls}
      />
    </div>
  );
}
